import React, { useRef, useCallback, useState } from 'react';
import { Popover, Icon } from '@vectord/components';
import { TypeUtils } from '@vectord/utils';
import { useOutsideClick } from '@vectord/hooks';
import PropTypes from 'prop-types';

import SortableTableCellOption from 'components/SortableTableCellOption';
import styles from './SortableTableCell.module.scss';
import TableCell from 'components/TableCell';

const placementsMap = {
  bottom: { top: '160%', right: '-60%' },
};

function SortableTableCell({
  children,
  field,
  descLabel = 'High to Low',
  ascLabel = 'Low to High',
  sortValue,
  sortField,
  onSortChange,
}) {
  const popoverRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setIsOpen((prevOpen) => !prevOpen);
  }, []);

  const handleOutsideClick = useCallback(
    (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleSelect = useCallback(
    (field, value) => {
      setIsOpen(false);
      if (TypeUtils.isFunction(onSortChange)) {
        onSortChange(field, value);
      }
    },
    [onSortChange]
  );

  const handleReset = useCallback(() => {
    setIsOpen(false);
    if (TypeUtils.isFunction(onSortChange)) {
      onSortChange(null, null);
    }
  }, [onSortChange]);

  useOutsideClick(sortDropdownRef, handleOutsideClick);

  return (
    <>
      <TableCell className={styles.head}>
        <div>
          <span className={styles.heading}>{children}</span>
          <Icon
            ref={sortDropdownRef}
            name={
              sortField === field && sortValue ? `sort-${sortValue}` : 'sort'
            }
            onClick={handleToggle}
            className="p-l-xxs cursor"
          />
        </div>
      </TableCell>
      <Popover
        ref={popoverRef}
        anchorRef={sortDropdownRef}
        isOpen={isOpen}
        location={placementsMap['bottom']}
        onClose={handleClose}
      >
        <div className={styles.sortByPopover}>
          <Icon
            name="clear"
            className={styles.closeIcon}
            onClick={handleClose}
          />
          <div className="bold dark h5 p-b-xxs">Sort By</div>
          <SortableTableCellOption
            value="asc"
            field={field}
            isSelected={sortField === field && sortValue === 'asc'}
            handleSelect={handleSelect}
          >
            {ascLabel}
          </SortableTableCellOption>
          <SortableTableCellOption
            value="desc"
            field={field}
            handleSelect={handleSelect}
            isSelected={sortField === field && sortValue === 'desc'}
          >
            {descLabel}
          </SortableTableCellOption>
          <SortableTableCellOption
            value="null"
            field={field}
            handleReset={handleReset}
            isDisabled={!sortValue}
          >
            reset
          </SortableTableCellOption>
        </div>
      </Popover>
    </>
  );
}

SortableTableCell.propTypes = {
  children: PropTypes.node,
  field: PropTypes.string,
  descLabel: PropTypes.string,
  ascLabel: PropTypes.string,
  sortValue: PropTypes.string,
  sortField: PropTypes.string,
  onSortChange: PropTypes.func,
};

export default SortableTableCell;
